import {
  type EffectiveMenu,
  newId,
  type OrderLineInput,
  type OrderPrefill,
  type QuantityWarning,
  type StaffOrderView,
} from "@kitchen/schemas";
import { Button, Chip, faDigits, formatDate, formatTime, Panel, Seg } from "@kitchen/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type ContactRow } from "../../api.js";
import { addDays, lineKey, PHASE_LABEL, STATUS_LABEL, tehranToday } from "../../labels.js";
import { useMeals, useOrgs, useTiers } from "../menu/data.js";

const toAscii = (s: string) =>
  s
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
const PREFILL_LABEL: Record<OrderPrefill["source"], string> = {
  template: "از الگوی همیشگی واحد",
  "same-weekday": "مثل همین روز هفته‌ی قبل",
  "last-order": "مثل آخرین سفارش",
  none: "سفارش قبلی ندارد",
};

/**
 * F3-T15: staff take a unit's order on the phone or from a paper sheet (design v2 PhoneOrder.html, DL-021):
 * find the unit, prefilled form with differences coloured, keyboard first. Nothing is submitted without the
 * explicit button (DL-013); after the cook lock it is still taken and flagged late.
 */
export function PhoneOrderPage() {
  const { units } = useOrgs();
  const meals = useMeals();
  const [query, setQuery] = useState("");
  const [unitId, setUnitId] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const [date, setDate] = useState(tehranToday);
  const [mealId, setMealId] = useState<string | null>(null);
  const meal = mealId ?? meals.data?.find((m) => m.active)?.id ?? null;
  const search = useRef<HTMLInputElement>(null);

  // Contacts are loaded only when the query looks like a phone number (4+ digits).
  const digits = toAscii(query).replace(/\D/g, "");
  const contacts = useQuery({
    queryKey: ["all-contacts", units.length],
    queryFn: async () => {
      const lists = await Promise.all(
        units.map((u) => api<ContactRow[]>(`/units/${u.id}/contacts`).catch(() => [] as ContactRow[])),
      );
      return units.flatMap((u, i) =>
        (lists[i] ?? []).map((c) => ({ unitId: u.id, phone: c.phone, name: c.name })),
      );
    },
    enabled: digits.length >= 4 && units.length > 0,
    staleTime: 300_000,
  });
  const matches = useMemo(() => {
    const q = toAscii(query).trim();
    if (!q) return [];
    const byName = units.filter((u) => toAscii(u.name).includes(q));
    const byPhone =
      digits.length >= 4
        ? (contacts.data ?? [])
            .filter((c) => c.phone.replace(/\D/g, "").includes(digits.replace(/^0/, "")))
            .map((c) => units.find((u) => u.id === c.unitId))
        : [];
    return [
      ...new Map([...byName, ...byPhone].filter((u) => u !== undefined).map((u) => [u.id, u])).values(),
    ].slice(0, 8);
  }, [query, units, contacts.data, digits]);

  // "/" jumps to the unit search from anywhere on the page.
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        search.current?.focus();
      }
    };
    addEventListener("keydown", on);
    return () => {
      removeEventListener("keydown", on);
    };
  }, []);

  const choose = (id: string) => {
    setUnitId(id);
    setQuery("");
  };
  const unit = units.find((u) => u.id === unitId);

  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", alignItems: "start" }}
    >
      <Panel title="واحد">
        <div className="stack">
          <div className="field">
            <label htmlFor="po-search">
              نام، کد یا شماره‌ی رابط <span className="t-cap muted">( / )</span>
            </label>
            <input
              id="po-search"
              ref={search}
              className="input"
              autoFocus
              value={query}
              role="combobox"
              aria-expanded={matches.length > 0}
              aria-controls="po-results"
              onChange={(e) => {
                setQuery(e.target.value);
                setCursor(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setCursor(Math.min(cursor + 1, matches.length - 1));
                }
                if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setCursor(Math.max(cursor - 1, 0));
                }
                if (e.key === "Enter" && matches[cursor]) {
                  e.preventDefault();
                  choose(matches[cursor].id);
                }
              }}
            />
          </div>
          <div id="po-results" role="listbox" className="items">
            {matches.map((u, i) => (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={i === cursor}
                className="item"
                style={{
                  width: "100%",
                  textAlign: "start",
                  background: i === cursor ? "var(--card)" : undefined,
                }}
                onClick={() => {
                  choose(u.id);
                }}
              >
                <span className="m">{faDigits(u.name)}</span>
              </button>
            ))}
          </div>
          {unit && (
            <div className="card">
              <div className="t-cap muted">سفارش برای</div>
              <div className="b">{faDigits(unit.name)}</div>
            </div>
          )}
          <Seg
            label="روز"
            value={date}
            onChange={setDate}
            options={[
              { value: tehranToday(), label: "امروز" },
              { value: addDays(tehranToday(), 1), label: "فردا" },
            ]}
          />
          <p className="t-cap muted">{formatDate(date)}</p>
          {meals.data && meal && (
            <Seg
              label="وعده"
              value={meal}
              onChange={setMealId}
              options={meals.data.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))}
            />
          )}
          <p className="t-cap muted">میان‌بُرها: «/» جست‌وجو · ↑↓ جابه‌جایی بین ردیف‌ها · Ctrl+Enter ثبت</p>
        </div>
      </Panel>
      {unit && meal ? (
        <OrderSheet
          key={`${unit.id}|${date}|${meal}`}
          unitId={unit.id}
          unitName={unit.name}
          date={date}
          mealId={meal}
          onDone={() => {
            setUnitId(null);
            search.current?.focus();
          }}
        />
      ) : (
        <Panel>
          <p className="muted">واحد را پیدا کنید؛ فرم با سفارش همیشگی‌اش پر می‌شود.</p>
        </Panel>
      )}
    </div>
  );
}

function OrderSheet({
  unitId,
  unitName,
  date,
  mealId,
  onDone,
}: {
  unitId: string;
  unitName: string;
  date: string;
  mealId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const tiers = useTiers();
  const menu = useQuery({
    queryKey: ["effective-menu", unitId, date, mealId],
    queryFn: () => api<EffectiveMenu>(`/menu/effective?unitId=${unitId}&date=${date}&mealId=${mealId}`),
  });
  const prefill = useQuery({
    queryKey: ["staff-prefill", unitId, date, mealId],
    queryFn: () => api<OrderPrefill>(`/orders/prefill?unitId=${unitId}&date=${date}&mealId=${mealId}`),
  });
  const existing = useQuery({
    queryKey: ["staff-existing", unitId, date, mealId],
    queryFn: async () => {
      const day = await api<{ id: string; orderingUnitId: string; mealId: string }[]>(
        `/orders?date=${date}&mealId=${mealId}`,
      );
      const row = day.find((o) => o.orderingUnitId === unitId);
      return row ? api<StaffOrderView>(`/orders/${row.id}`) : null;
    },
  });
  const windowInfo = useQuery({
    queryKey: ["staff-window", unitId, date, mealId],
    queryFn: () =>
      api<{ phase: "open" | "late" | "locked"; deadlineAt: string; cookLockAt: string }>(
        `/orders/window?unitId=${unitId}&date=${date}&mealId=${mealId}`,
      ),
  });

  const o = existing.data;
  const base = useMemo(() => {
    const m: Record<string, number> = {};
    if (o) for (const l of o.lines) m[lineKey(l)] = l.orderedQuantity;
    else for (const l of prefill.data?.lines ?? []) m[lineKey(l)] = l.quantity;
    return m;
  }, [o, prefill.data]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [note, setNote] = useState("");
  const [source, setSource] = useState<"phone" | "paper">("phone");
  const [warnings, setWarnings] = useState<QuantityWarning[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const tierName = (id: string | null) => (id ? (tiers.data?.find((t) => t.id === id)?.name ?? "") : "");

  const rows = (menu.data?.items ?? []).flatMap((item) =>
    (item.tierIds.length ? item.tierIds : [null]).map((tierId) => ({
      key: lineKey({ menuItemId: item.menuItemId, tierId }),
      item,
      tierId,
    })),
  );
  const value = (k: string) => counts[k] ?? base[k] ?? 0;
  const lines: OrderLineInput[] = rows
    .filter((r) => value(r.key) > 0)
    .map((r) => ({
      menuItemId: r.item.menuItemId,
      tierId: r.tierId,
      quantity: value(r.key),
      note: "",
      extra: false,
    }));
  const portions = lines.filter((l) => l.tierId !== null).reduce((n, l) => n + l.quantity, 0);
  const editable = !o || ["draft", "submitted", "returned", "approved"].includes(o.status);

  async function submit(confirmed: boolean) {
    setBusy(true);
    setMessage(null);
    try {
      if (o && !editable) {
        const reason = window.prompt(
          "سفارش در حال آماده‌سازی است؛ دلیل تغییر؟ (آشپزخانه فوری خبردار می‌شود)",
        );
        if (!reason?.trim()) return;
        await api(`/orders/${o.id}/adjust`, { body: { version: o.version, lines, reason: reason.trim() } });
      } else {
        await api("/orders", {
          body: {
            id: o?.id ?? newId(),
            orderingUnitId: unitId,
            businessDate: date,
            mealId,
            lines,
            contactNote: note,
            source,
            confirmedWarnings: confirmed,
            ...(o ? { version: o.version } : {}),
          },
        });
      }
      setWarnings(null);
      await qc.invalidateQueries({ queryKey: ["orders-review"] });
      window.alert(`سفارش ${faDigits(unitName)} ثبت شد (${faDigits(portions)} پرس).`);
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "confirm-quantities") {
        setWarnings((err.body as { warnings?: QuantityWarning[] } | null)?.warnings ?? []);
        setMessage(err.message);
      } else if (err instanceof ApiError && (err.code === "exists" || err.code === "version-conflict")) {
        await existing.refetch();
        setMessage("این واحد همین حالا سفارش دارد؛ فرم با سفارش فعلی پر شد. دوباره بررسی و ثبت کنید.");
        setCounts({});
      } else {
        setMessage(err instanceof ApiError ? err.message : "ارتباط برقرار نشد");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={faDigits(unitName)}
      actions={
        o ? (
          <Chip tone="warn">{STATUS_LABEL[o.status]} — ویرایش سفارش موجود</Chip>
        ) : (
          prefill.data && <Chip plain>{PREFILL_LABEL[prefill.data.source]}</Chip>
        )
      }
    >
      <div
        className="stack"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && lines.length) {
            e.preventDefault();
            void submit(Boolean(warnings));
          }
        }}
      >
        {windowInfo.data && (
          <p
            className="t-cap"
            style={windowInfo.data.phase === "open" ? undefined : { color: "var(--warn-ink)" }}
          >
            {PHASE_LABEL[windowInfo.data.phase]} · مهلت {formatTime(windowInfo.data.deadlineAt)} · قفل پخت{" "}
            {formatTime(windowInfo.data.cookLockAt)}
            {windowInfo.data.phase !== "open" && " — ثبت می‌شود و علامت دیرهنگام می‌خورد"}
          </p>
        )}
        <Seg
          label="منبع"
          value={source}
          onChange={setSource}
          options={[
            { value: "phone", label: "تلفنی" },
            { value: "paper", label: "برگه‌ی کاغذی" },
          ]}
        />
        {menu.data && rows.length === 0 && <p className="muted">برای این روز و وعده منویی ثبت نشده است.</p>}
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>غذا</th>
                <th>سطح</th>
                <th className="num">تعداد</th>
                <th className="num">قبلی</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const prev = base[r.key] ?? 0;
                const now = value(r.key);
                const changed = counts[r.key] !== undefined && now !== prev;
                const wn = warnings?.find((x) => lineKey(x) === r.key);
                return (
                  <tr key={r.key} style={changed ? { background: "var(--blue-soft)" } : undefined}>
                    <td>
                      {r.item.name}
                      {wn && (
                        <div className="t-cap" style={{ color: "var(--warn-ink)" }}>
                          معمولاً {faDigits(wn.usual)}
                        </div>
                      )}
                    </td>
                    <td>{tierName(r.tierId)}</td>
                    <td className="num">
                      <input
                        ref={(el) => {
                          inputs.current[i] = el;
                        }}
                        className="input num"
                        style={{ width: 96 }}
                        inputMode="numeric"
                        aria-label={`تعداد ${r.item.name} ${tierName(r.tierId)}`}
                        value={faDigits(now)}
                        onFocus={(e) => {
                          e.target.select();
                        }}
                        onChange={(e) => {
                          setCounts({
                            ...counts,
                            [r.key]: Math.min(
                              10_000,
                              Number(toAscii(e.target.value).replace(/\D/g, "") || "0"),
                            ),
                          });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown" || (e.key === "Enter" && !e.ctrlKey)) {
                            e.preventDefault();
                            inputs.current[i + 1]?.focus();
                          }
                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            inputs.current[i - 1]?.focus();
                          }
                          if (e.key === "+") {
                            e.preventDefault();
                            setCounts({ ...counts, [r.key]: now + 1 });
                          }
                          if (e.key === "-") {
                            e.preventDefault();
                            setCounts({ ...counts, [r.key]: Math.max(0, now - 1) });
                          }
                        }}
                      />
                    </td>
                    <td className="num muted">{prev ? faDigits(prev) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="field">
          <label htmlFor="po-note">یادداشت سفارش</label>
          <input
            id="po-note"
            className="input"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </div>
        <div className="row-between">
          <span className="muted">جمع</span>
          <span className="b">{faDigits(portions)} پرس</span>
        </div>
        {message && (
          <p className="ink2" role="status">
            {message}
          </p>
        )}
        <div className="row" style={{ gap: "var(--s2)", justifyContent: "flex-end" }}>
          {warnings && (
            <Button
              variant="ghost"
              onClick={() => {
                setWarnings(null);
                setMessage(null);
              }}
            >
              اصلاح می‌کنم
            </Button>
          )}
          <Button
            variant="primary"
            size="lg"
            icon="check"
            disabled={busy || lines.length === 0}
            onClick={() => {
              void submit(Boolean(warnings));
            }}
          >
            {warnings ? "بله، درست است؛ ثبت" : o ? "ثبت تغییرات" : "ثبت سفارش"}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
